/* eslint-disable react/self-closing-comp */
import type { FC } from 'react';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { styled, cssMap } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl';
import invariant from 'tiny-invariant';
import ReactDOM from 'react-dom';

import { Card } from '@atlaskit/smart-card/card/lazy';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import {
	ActionName,
	SmartLinkPosition,
	SmartLinkDirection,
	SmartLinkSize,
} from '@atlaskit/smart-card/enums';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { Popup } from '@atlaskit/popup/popup';
import Blanket from '@atlaskit/blanket/blanket';
import { token } from '@atlaskit/tokens';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { scrollJustEnoughIntoView } from '@atlaskit/pragmatic-drag-and-drop/utils/scroll-just-enough-into-view';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/utils/set-custom-native-drag-preview';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/utils/pointer-outside-of-preview';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import { LoadableLazy } from '@confluence/loadable';
import { useMatchRoute } from '@confluence/route-manager/entry-points/RouteState.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { hashData } from '@confluence/hash';

import type { useShortcutsSpaceNavigationQuery_quick } from './useShortcuts.tsx';
import { useEditShortcut } from './useShortcuts.tsx';

const LinkPickerWithPlugin = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-LazyLinkPickerWithPlugin" */ './LinkPickerWithPlugin.tsx'
			)
		).LinkPickerWithPlugin,
});

export const isPageSSRd = (): string | boolean | undefined =>
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	process.env.REACT_SSR || window.__SSR_RENDERED__;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragPreview = styled.div({
	'--grid': '8px',
	padding: 'var(--grid) calc(var(--grid) * 2)',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	boxSizing: 'border-box',
	width: '210px',
	background: token('elevation.surface.raised'),
	justifyContent: 'space-between',
	color: token('color.text.subtle'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& a': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text.subtle')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& .actions-button-group': {
		display: 'none',
	},
});

const itemStyles = cssMap({
	base: {
		/* Match Nav4 style */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&': {
			position: 'relative',
			height: '32px',
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
			borderRadius: token('radius.small', '3px'),
			marginTop: '0',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:hover, &:has(:focus)': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-smart-block="true"]': {
			padding: '4px 4px 4px 8px',
			gap: 'initial',
		},
		/* Add a little notch on the left side like Nav4 */
		'&:hover::before': {
			backgroundColor: token('color.background.neutral.hovered'),
			content: ' ',
			position: 'absolute',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '10px',
			width: '2px',
			height: '12px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a': {
			/* Override the default link color and size */
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text.subtle')} !important`,
			font: token('font.body'),
			fontWeight: token('font.weight.medium'),
			/* Position the unresolved link the same as the resolved link */
			paddingBlock: '0',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			paddingLeft: '34px',
			paddingRight: token('space.400'),
			/* Add ... to long unresolved links */
			display: 'block',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a[data-smart-element="Title"]': {
			paddingInline: token('space.100'),
			display: '-webkit-box',
			/* Restore the title ellipsis for long resolved links */
			whiteSpace: 'normal',
		},
		/* Prevent underlined links */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a:hover': {
			textDecoration: 'none',
		},
		/* Prevent the hidden link from showing */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-smart-link-container="true"] > a': {
			padding: '0',
		},
		/* Fix vertical alignment for links without ... button */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-smart-block="true"]': {
			height: '24px',
		},
		/* Fix spacing for unresolved links */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-smart-block="true"] > *': {
			minWidth: 'auto',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-smart-element-icon="true"] img': {
			filter: 'grayscale(1)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& [data-smart-element-icon="true"] svg *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			fill: `${token('color.icon.subtle')} !important`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& button': {
			width: '24px',
			height: '24px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& button::after': {
			borderWidth: '0',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'& button:not(:hover)': {
			backgroundColor: 'transparent',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text.subtle')} !important`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& button svg': {
			width: '12px',
			height: '12px',
		},
	},
	dragging: {
		opacity: '0.6',
	},
	isMenuOpen: {
		/* Match Nav4 actions style */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&&&': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupTrigger = styled.span({
	float: 'right',
	maxWidth: 0,
	maxHeight: 0,
	visibility: 'hidden',
	overflow: 'hidden',
});

const i18n = defineMessages({
	shortcutsQuickActionsRemoveButton: {
		id: 'space-shortcuts.shortcuts.quick-actions.remove',
		defaultMessage: 'Remove',
		description: 'Label for the button that removes a shortcut from the sidebar',
	},
	draggableItemLabel: {
		id: 'space-shortcuts.draggable-item.label',
		description: 'aria-label for the draggable page tree item',
		defaultMessage: `{title} draggable item`,
	},
});

type DraggableState = 'idle' | 'generate-preview' | 'dragging';

type ShortcutsItemProps = {
	item: useShortcutsSpaceNavigationQuery_quick;
	index: number;
	spaceId: string | null;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	isSpaceAdmin: boolean;
	links: useShortcutsSpaceNavigationQuery_quick[];
	deleteShortcut: (item: useShortcutsSpaceNavigationQuery_quick) => void;
	onDragStart: any;
	onDragEnd: any;
};

//not exported from smartCard for some reason
type AnchorTarget = '_blank' | '_self' | '_top' | '_parent';

const SHORTCUTS_ITEM_TYPE = 'shortcuts-item';

// string begins with 1+ characters other than "/", has a "/", and is followed by one character other than "/"
// OR string begins with "/", is followed by 1 character other than "/", ends with any # of any character
// while ignoring capitalization
const relativeUrlMatch = new RegExp(`^[^/]+/[^/].*$|^/[^/].*$`, 'i');

// adding domain to relative confluence URLs so smart links don't error
export const getFullUrl = (url: any, isExternal: any): any => {
	if (url === null) {
		return url;
	}
	if (!isExternal && relativeUrlMatch.test(url)) {
		return window.location.origin + url;
	}
	// edge case that doesn't match regexp
	if (url.match(`${window.location.protocol}///wiki`)) {
		return window.location.origin + url.replace(`${window.location.protocol}//`, '');
	}
	return url;
};

export const ShortcutsItemComponent: FC<ShortcutsItemProps & WithFlagsProps> = ({
	flags,
	item,
	index,
	spaceId,
	createAnalyticsEvent,
	isSpaceAdmin,
	links,
	deleteShortcut,
	onDragStart,
	onDragEnd,
}) => {
	const ref = useRef<HTMLLIElement | null>(null);
	const refPreview = useRef<HTMLDivElement | null>(null);
	const intl = useIntl();
	const CardComponent = isPageSSRd() ? CardSSR : Card;

	let { url, styleClass } = item;
	url = url || ('' as string);
	const { title = '', id } = item;
	//styleClass is the old way of knowing if a link was internal or external
	styleClass = styleClass || '';

	const matchRoute = useMatchRoute();

	let target: AnchorTarget = '_blank',
		navdexPointType: 'navigationSessionStartTouch' | null = null;
	try {
		const isRelativeUrl = matchRoute(url);
		const includesHostName = url.includes(window.location.hostname);
		const containsExternalStyleClass = styleClass.indexOf('external_link') >= 0;
		//now we also check if the link is relative or contains the pathname, falls back to styleclass
		const isExternal = !(isRelativeUrl || includesHostName) && containsExternalStyleClass;
		url = getFullUrl(url, isExternal) || '';
		target = isExternal ? '_blank' : '_self';
		navdexPointType = !isExternal ? START_TOUCH : null;
	} catch {
		// eslint-disable-next-line no-console
		console.warn('malformed URL:', url);
		//prefer to show a different UI when the link is bad, letting the user know they should update the shortcut...
		//however the smartcard format doesn't really have a way to do this.
	}

	const [isEditOpen, setIsEditOpen] = useState(false);
	const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [draggableState, setDraggableState] = useState<DraggableState>('idle');

	const handleClose = useCallback(() => {
		setIsEditOpen(false);
	}, [setIsEditOpen]);

	const openEdit = useCallback(() => {
		setIsEditOpen(!isEditOpen);
	}, [isEditOpen, setIsEditOpen]);

	const handleSubmit = useEditShortcut(links, flags, spaceId);

	const onSubmit = useCallback(
		(href: string, displayText: string | null) => {
			handleSubmit({
				id,
				url: href,
				title: displayText || '',
			});
			handleClose();
		},
		[handleClose, handleSubmit, id],
	);

	const location = 'confluenceShortcuts';

	const sendShortcutClickedAnalytics = (navdexPointType) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'spaceShortcut',
				source: 'containerNavigation',
				containerType: 'space',
				containerId: spaceId,
				attributes: {
					navVersion: '3',
					...(navdexPointType && { navdexPointType }),
					location,
				},
			},
		}).fire();
	};

	useEffect(() => {
		if (!isSpaceAdmin) {
			return;
		}
		invariant(ref.current);
		return combine(
			draggable({
				element: ref.current,
				onGenerateDragPreview: ({ source, nativeSetDragImage }) => {
					scrollJustEnoughIntoView({ element: source.element });
					setCustomNativeDragPreview({
						getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
						render: ({ container }) => {
							/* oxlint-disable-next-line react-js/no-deprecated */ // eslint-disable-next-line react/no-deprecated
							ReactDOM.render(<DragPreview ref={refPreview} />, container);
							if (refPreview.current) {
								refPreview.current.innerHTML = ref.current?.innerHTML ?? '';
							}
							/* oxlint-disable-next-line react-js/no-deprecated */ // eslint-disable-next-line react/no-deprecated
							return () => ReactDOM.unmountComponentAtNode(container);
						},
						nativeSetDragImage,
					});
				},
				onDragStart: () => {
					setDraggableState('dragging');
					onDragStart && onDragStart();
				},
				getInitialData: () => ({
					id: item.id,
					type: SHORTCUTS_ITEM_TYPE,
				}),
				// attach a url for other documents and applications
				getInitialDataForExternal: () => {
					if (!url) {
						return {};
					}
					return {
						'text/uri-list': url,
					};
				},
				onDrop: () => {
					setDraggableState('idle');
				},
			}),
			dropTargetForElements({
				element: ref.current,
				canDrop: ({ source }) => source.data.type === SHORTCUTS_ITEM_TYPE,
				getData: ({ input, element }) => {
					const data = {
						id: item.id,
						type: SHORTCUTS_ITEM_TYPE,
						index,
					};
					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges: ['top', 'bottom'],
					});
				},
				onDrag: ({ self, source, location }) => {
					if (
						source.data.id !== item.id &&
						location?.current?.dropTargets[0]?.data?.id === item.id
					) {
						setClosestEdge(extractClosestEdge(self.data));
					} else {
						setClosestEdge(null);
					}
				},
				onDragLeave: () => {
					setClosestEdge(null);
				},
				onDrop: ({ location }) => {
					if (location?.current?.dropTargets[0]) {
						const { data: target } = location.current.dropTargets[0];
						const { data: source } = location.initial.dropTargets[0];
						const edge: Edge | null = extractClosestEdge(target);
						if (
							!target ||
							!target.id ||
							target.type !== SHORTCUTS_ITEM_TYPE ||
							(target.id === item.id && !edge)
						) {
							return;
						}
						const sourcePosition = {
							index: source.index as number,
						};

						const destinationPosition = {
							index: getReorderDestinationIndex({
								startIndex: source.index as number,
								indexOfTarget: target.index as number,
								closestEdgeOfTarget: edge,
								axis: 'vertical',
							}),
						};
						if (sourcePosition.index !== destinationPosition.index) {
							onDragEnd(sourcePosition, destinationPosition);
						}
					}
					setClosestEdge(null);
				},
			}),
		);
	}, [isSpaceAdmin, onDragStart, onDragEnd, item, index, url]);

	const disableNativeDragOnLinks = () => {
		if (!ref.current) return;
		Array.from(ref.current.querySelectorAll('a')).forEach((anchor) =>
			anchor.setAttribute('draggable', 'false'),
		);
	};

	const handleComplete = () => {
		disableNativeDragOnLinks();
	};

	const handleCompleteError = () => {
		disableNativeDragOnLinks();
	};

	const hash = hashData(`${title}-${url}`);
	const placeholderId = `shortcut-item-${index}-${hash}`;

	const itemContent = (
		// TODO: Add `shouldRenderToParent={true}` to meet a11y requirements. See
		// https://a11y.atlassian.net/browse/JCATS-788
		// https://hello.jira.atlassian.cloud/browse/DISCO-4293
		<>
			<CardComponent
				testId="shortcut-item"
				appearance="inline"
				url={url || ''}
				placeholder={title || undefined}
				onClick={sendShortcutClickedAnalytics(navdexPointType)}
				onResolve={handleComplete}
				onError={handleCompleteError}
				ui={{
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					clickableContainer: true,
					size: SmartLinkSize.Medium,
				}}
			>
				<TitleBlock
					direction={SmartLinkDirection.Horizontal}
					maxLines={1}
					position={SmartLinkPosition.Center}
					text={title || undefined}
					anchorTarget={target}
					hideTitleTooltip
					actions={
						isSpaceAdmin
							? [
									{
										name: ActionName.EditAction,
										onClick: openEdit,
										iconPosition: 'before',
										testId: 'editAction',
									},
									{
										name: ActionName.DeleteAction,
										onClick: () => deleteShortcut(item),
										iconPosition: 'before',
										testId: 'deleteAction',
										content: intl.formatMessage(i18n.shortcutsQuickActionsRemoveButton),
									},
								]
							: undefined
					}
					showActionOnHover
					onActionMenuOpenChange={({ isOpen }) => {
						setIsActionMenuOpen(isOpen);
					}}
					placeholderId={placeholderId}
				/>
			</CardComponent>
			{(isEditOpen || isActionMenuOpen) && (
				<>
					<Popup
						autoFocus={false}
						isOpen={isEditOpen}
						onClose={() => handleClose()}
						content={(props) => (
							<div>
								<LinkPickerWithPlugin
									{...props}
									onSubmit={({ url, displayText }) => {
										onSubmit(url, displayText);
									}}
									onCancel={handleClose}
									url={url || ''}
									displayText={title || ''}
								/>
							</div>
						)}
						placement="bottom-start"
						offset={[-42, 4]}
						// the trigger is hidden
						trigger={(triggerProps) => <PopupTrigger {...triggerProps}></PopupTrigger>}
					/>
					<Blanket
						testId="space-shortcuts-actions-blanket"
						isTinted={false}
						shouldAllowClickThrough={!isEditOpen && !isActionMenuOpen}
						onBlanketClicked={() => {
							setIsActionMenuOpen(false);
							setIsEditOpen(false);
						}}
					/>
				</>
			)}
			{closestEdge ? <DropIndicator edge={closestEdge} /> : null}
		</>
	);

	const isMenuOpen = isActionMenuOpen || isEditOpen;

	return (
		<Box
			as="li"
			ref={ref}
			aria-label={intl.formatMessage(i18n.draggableItemLabel, { title })}
			xcss={cx(itemStyles.base, itemStyles[draggableState], isMenuOpen && itemStyles.isMenuOpen)}
		>
			{itemContent}
		</Box>
	);
};

export const DraggableShortcutsItem: React.ComponentType<Omit<ShortcutsItemProps, 'flags'>> =
	withFlags(ShortcutsItemComponent);
